import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import Cookies from 'js-cookie';
import styled from 'styled-components';

import { ERROR, PASSWORD_TYPE_SHORT_VERSION } from '../../_constants';
import { IModal, ILogin } from 'customTypes';
import { IPageInfo, IUser, pageInfoState, userState } from '../../atoms';
import { ModalBackground, Modal } from '../../assets/styles/Modal';
import { BtnBlue, BtnGhost } from '../Button';
import Input from '../Input';
import { formatDate } from '../../utils/formatDate';
import { isPasswordRegExpPass } from '../../utils/regExp';
import { usePasswordValidationCheck } from '../../utils/usePasswordValidationCheck';
import { logPost } from '../../services/log';
import { registerPwd, registerPwdchangeDate } from '../../services/register';

interface IPasswordState {
  password: string;
  passwordConfirm: string;
}

// 공통 Modal 스타일의 inputForm-body 글자색(#121212)이 고정값이라 다크모드에서 보이지 않아 테마 색상으로 덮어씀
const Root = styled.div`
  &.modal.fitHeight > .inputForm-body {
    color: var(--text-primary);
    .inputFormText {
      background-color: var(--tonal-blue);
    }
  }
`;

const PasswordChangeTermModal = ({ openModal, setOpenModal, state, setState, resetState }: ILogin) => {
  const { t } = useTranslation();
  const setUserInfo = useSetRecoilState<IUser>(userState);
  const setPageInfo = useSetRecoilState<IPageInfo>(pageInfoState);
  const focusRef = useRef<HTMLInputElement>(null);
  const [pwAlert, setPwAlert] = useState(false);
  const [newPassword, setNewPassword] = useState<IPasswordState>({
    password: '',
    passwordConfirm: '',
  });
  const limitMonth = state && Math.floor(state.pwdLimitTerm / 30);
  const yyyymmdd = formatDate(state?.userInfo.pwdChangeDate);

  const { isPasswordPassed, passwordAlertMessage } = usePasswordValidationCheck(newPassword.password, newPassword.passwordConfirm);

  // 모달창닫기
  const onClickClose = () => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  };

  // 패스워드 input에서 포커스 아웃되었을 때 비밀번호정규식 확인문구 노출
  const onBlur = () => {
    if (!isPasswordRegExpPass(newPassword?.password)) setPwAlert(true);
    else setPwAlert(false);
  };

  // 정상 로그인과 동일하게 토큰 쿠키, 사용자정보, 메뉴권한 저장
  // (userInfo, refreshToken이 빠지면 메뉴 조회가 userId 없이 요청되고 토큰 갱신도 실패해 로그인화면으로 튕김)
  const completeLogin = () => {
    Cookies.set('prosafeToken', state.accessToken);
    state.refreshToken && Cookies.set('refreshToken', state.refreshToken);
    Cookies.set('dashboardViewer', 'N');
    setUserInfo((prev) => ({
      ...prev,
      ...state.userInfo,
      userMenuList: state.userMenuList,
    }));
    setPageInfo((prev) => ({ ...prev, header: { mtName: '대시보드', mdName: '' }, mtCd: '', mdCd: '', mrCd: '' }));
  };

  // 새 비밀번호 DB insert API 호출
  const saveNewPasswordAPI = async () => {
    const res = await registerPwd(state?.id, newPassword?.password, state.accessToken);
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      toast.success(t(message));
      const { userId, hCd, sCd } = state.userInfo;
      completeLogin();
      await logPost({
        hCd,
        sCd,
        userId,
        menu: '로그인',
        action: '비밀번호 변경',
        etc: ``,
      });
      window.location.replace('/');
    } else if (statusCode === 4014) {
      toast.error(t(message));
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 새 비밀번호 저장버튼 클릭시
  // 1. 비밀번호 빈값,두 비밀번호 일치확인. 불일치시 알럿메시지
  // 2. 1번 통과시 정규식체크하고 통과시 새 비밀번호 저장 API 호출. 정규식 미충족시 비밀번호 인풋으로 포커스이동
  const onClickSaveNewPassword = () => {
    const { password } = newPassword;

    if (isPasswordPassed) {
      if (isPasswordRegExpPass(password)) {
        saveNewPasswordAPI();
      } else {
        toast.warning(t('비밀번호 조건을 확인하세요'));
        focusRef.current?.focus();
      }
    } else toast.warning(t(passwordAlertMessage));
  };

  const onClickChangePasswordLater = async () => {
    const res = await registerPwdchangeDate(state.id, state.accessToken);
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      completeLogin();
      window.location.replace('/');
    } else {
      // toast.error(t(ERROR));
    }
  };

  return (
    <ModalBackground>
      <Modal>
        <Root className='modal fitHeight'>
          <div className='inputForm-head'>
            <h5 className='bold'>{openModal?.title}</h5>
          </div>
          <div className='inputForm-body'>
            <div className='modal-contents'>
              <div className='inputForm-col'>
                <div className='inputFormText'>
                  {t('사용자님의 소중한 개인정보를 안전하게 보호하기 위해')}
                  <span className='bold'> {t('비밀번호 변경을 안내드립니다.')}</span>
                  <br /> {t('타인으로부터 개인정보를 보호하기 위하여 비밀번호를 주기적으로 변경해주시기 바랍니다.')}
                  <br />
                  <div className='content'>
                    <div className='bold'>{t('안전한 비밀번호를 만들기 위해서는?')}</div>
                    <div>
                      - <span className='red'>{t('영문 대문자, 소문자, 숫자, 특수문자')}</span> {t('3종류 조합해서 최소 8자 이상 설정')}
                    </div>
                    <div>- {t('생일, 주민등록번호, 전화번호 등 개인정보가 포함된 비밀번호는 피해서 설정')}</div>
                    <div>
                      - <span className='red'>{t('3개월에 1번')}</span> {t('이상 주기적으로 변경')}
                    </div>
                  </div>
                </div>
              </div>
              <div className='inputForm-col'>
                <div className='inputForm-row flexStart'>
                  <label htmlFor='userId' className='flex30'>
                    {t('사용자 ID')}
                  </label>
                  <div className='flex70'>{state?.userInfo.userId}</div>
                </div>
                <div className='inputForm-row flexStart'>
                  <label htmlFor='userName' className='flex30'>
                    {t('사용자명')}
                  </label>
                  <div className='flex70'>{state?.userInfo.userName}</div>
                </div>
                <div className='inputForm-row flexStart'>
                  <label htmlFor='pwdChangeDate' className='flex30'>
                    {t('이전 변경일시')}
                  </label>
                  <div className='flex70'>{yyyymmdd}</div>
                </div>
                <div className='inputForm-col labelRow'>
                  <Input name='password' label={t('신규 비밀번호')} type='password' state={newPassword} setState={setNewPassword} placeholder='' blur={onBlur} inputRef={focusRef} />
                </div>
                <div className='flexColumn'>
                  <small className='red text-left'>{pwAlert && t(PASSWORD_TYPE_SHORT_VERSION)}</small>
                </div>
                <div className='inputForm-col labelRow'>
                  <Input name='passwordConfirm' label={t('비밀번호 확인')} type='password' state={newPassword} setState={setNewPassword} placeholder='' />
                </div>
              </div>
            </div>
            <div className='modal-footer flex-end'>
              <BtnBlue onClick={onClickSaveNewPassword}>{t('입력 완료')}</BtnBlue>
              <BtnGhost onClick={onClickChangePasswordLater}>
                {limitMonth}
                {t('개월 후에 변경하기')}
              </BtnGhost>
            </div>
          </div>
        </Root>
      </Modal>
    </ModalBackground>
  );
};

export default PasswordChangeTermModal;
